/**
 * @author: lv.chen
 * @date: 2025/12/1
 * @desc: CasualDailyReports页面组件
 */
import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl } from 'umi';
import { useMount } from 'ahooks';
import { Button } from 'tea-component';
import Bubble from '@home/components/TeaComponents/Bubble';
import Loading from '@intelligence/components/Loading';
import DateSelect from './components/DateSelect';
import CasualDailyReportsHeader from './components/Header';
import CasualDailyReportsHighlight from './components/Highlight';
import ProductChangedRank from './components/ProductChangedRank';
import ProductChangedCard from './components/ProductChangedCard';
import GrcReport from './components/GrcReport';
import CasualDailyReportsManufacturerChanged from './components/ManufacturerChanged';
import CasualDailyReportsIndustryNews from './components/IndustryNews';
import CasualDailyReportsRanks from './components/Ranks';
import CasualDailyReportsFooter from './components/Footer';
import useSearchParams from '@intelligence/hooks/useSearchParams';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import {
  fetchCasualDailyReportsDates,
  fetchCasualDailyReportDetails,
} from '@intelligence/v3/services/casualDailyReports';
import { gotoIntelligenceGame } from '@home/utils/utils';
import { downloadFile } from '@home/utils/file';
import { isEmpty } from '@intelligence/v3/utils/common';
import { screenshot } from '@intelligence/v3/utils/bom';
import { getSortedDates } from '@intelligence/v3/utils/date';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { getHostName, getInitialDate, CasualDailyReportData } from './utils';

import './index.scss';
import IMG_TITLE_PRODUCT from '@intelligence/assets/imgs/casualDailyReports/title-product.png';

interface ICasualDailyReportsProps extends IBaseProps {}

const CasualDailyReports: React.FC<ICasualDailyReportsProps> = (props) => {
  const { style, className } = props;

  const { formatMessage } = useIntl();

  const { date: urlDate } = useSearchParams();

  const markdownConverter = useRef(new CasualDailyReportData());
  const dailyReportPageRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [reportDates, setReportDates] = useState<string[]>([]);
  const [reportDate, setReportDate] = useState('');
  const [reportDetails, setReportDetails] = useState<ICasualDailyReportData | null>(null);

  const sensortowerDownloadChangeRank = reportDetails?.sensortower_download_change_rank;
  const sensortowerDauChangeRank = reportDetails?.sensortower_dau_change_rank;
  const sensortowerRevenueChangeRank = reportDetails?.sensortower_revenue_change_rank;
  const storeRankChangeRank = reportDetails?.store_rank_change_rank;
  const productChanged = reportDetails?.product_changed;
  const miniGameRankModules = reportDetails?.mini_game_rank_modules;
  const weixinPopularityStoreRankChangeRank = miniGameRankModules?.wechat_popularity;
  const weixinMostPlayedStoreRankChangeRank = miniGameRankModules?.wechat_most_played;
  const dyPopularityStoreRankChangeRank = miniGameRankModules?.douyin_popularity;
  const dyFreshGameStoreRankChangeRank = miniGameRankModules?.douyin_fresh_game;
  const showProductChanged = !isEmpty(productChanged);

  const weixinPopularityStoreRankTop = miniGameRankModules?.wechat_popularity_top;
  const weixinMostPlayedStoreRankTop = miniGameRankModules?.wechat_most_played_top;
  const dyPopularityStoreRankTop = miniGameRankModules?.douyin_popularity_top;
  const dyFreshGameStoreRankTop = miniGameRankModules?.douyin_fresh_game_top;
  const showWeixinStoreRankTop = !isEmpty(weixinPopularityStoreRankTop) || !isEmpty(weixinMostPlayedStoreRankTop);
  const showDyStoreRankTop = !isEmpty(dyPopularityStoreRankTop) || !isEmpty(dyFreshGameStoreRankTop);
  const showStoreRankTop = showWeixinStoreRankTop || showDyStoreRankTop;

  const updateReportDates = async (): Promise<string[]> => {
    const res = await fetchCasualDailyReportsDates({
      start_date: moment().subtract(2, 'years').startOf('year').format('YYYY-MM-DD'),
      end_date: moment().endOf('year').format('YYYY-MM-DD'),
    });
    const dates = getSortedDates(res?.data);
    setReportDates(dates);
    return dates;
  };

  const updateReportDetails = async (date: string) => {
    setLoading(true);
    setReportDate(date);
    updateURLSearchParams({ date });
    try {
      const res = await fetchCasualDailyReportDetails({ report_date: date });
      setReportDetails(res?.data?.data || null);
    } catch (error) {
      console.error(error);
      setReportDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReportDateChange = (date: string) => {
    void updateReportDetails(date);
  };

  const handleOpenNewTab = (url?: string) => {
    if (url) {
      reportButtonLog({
        ...dbiBtnLogMes.casualDailyReportsClickNews,
        extInfo: `Casual Daily Reports页面点击新闻链接：${url}`,
      });
      window.open(url);
    }
  };

  const handleGoToGameDetails = (game?: any) => {
    const entityType = game?.entity_type || '';
    const gameId = game?.game_id || '';

    if (entityType && gameId) {
      reportButtonLog({
        ...dbiBtnLogMes.casualDailyReportsClickGames,
        extInfo: `Casual Daily Reports页面点击游戏链接：${gameId}`,
      });
      gotoIntelligenceGame(gameId, entityType, 'overview');
    }
  };

  const handleGoToGameDetailsStats = (game?: any, tab = 'stats') => {
    const entityType = game?.entity_type || 'mobile';
    const gameId = game?.unified_id || '';

    if (entityType && gameId) {
      reportButtonLog({
        ...dbiBtnLogMes.casualDailyReportsClickGames,
        extInfo: `Casual Daily Reports页面点击游戏链接：${gameId}`,
      });
      gotoIntelligenceGame(gameId, entityType, tab);
    }
  };

  const handleDownloadImage = async () => {
    if (!dailyReportPageRef.current) {
      return;
    }

    setDownloadLoading(true);

    // 保存原始的 padding-bottom 值
    const originalPaddingBottom = dailyReportPageRef.current.style.paddingBottom;

    try {
      const fileName = `Casual_Daily_Report_${reportDate}.png`;
      reportButtonLog(dbiBtnLogMes.casualDailyReportsClickDownload);

      // 临时增加底部边距，确保截图时底部有留白
      // 使用 inline style 优先级最高，不会影响页面正常显示
      dailyReportPageRef.current.style.paddingBottom = '48px';

      // 等待样式生效
      await new Promise((resolve) => setTimeout(resolve, 100));

      const file = await screenshot(dailyReportPageRef.current, fileName);
      if (file) {
        downloadFile(file, fileName);
      }
    } catch (error) {
      console.error('下载图片失败:', error);
    } finally {
      // 恢复原始的 padding-bottom
      if (dailyReportPageRef.current) {
        dailyReportPageRef.current.style.paddingBottom = originalPaddingBottom;
      }
      setDownloadLoading(false);
    }
  };

  const initialize = async () => {
    setLoading(true);
    const dates = await updateReportDates();
    const date = getInitialDate({ dates, date: urlDate });
    void updateReportDetails(date);
  };

  useMount(() => {
    void initialize();
  });

  usePageLog('casualDailyReports');

  const renderLinker = (item?: any) => {
    const url = item?.url?.trim();
    if (!url) {
      return null;
    }

    return (
      <Bubble content="点击前往新闻原贴">
        <div className="dbi-url-link">
          <a
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleOpenNewTab(url);
            }}
          >
            {getHostName(url)}
          </a>
        </div>
      </Bubble>
    );
  };

  return (
    <div style={style} className={classnames('dbi-casual-daily-reports', className)}>
      {/* Download Button */}
      <div className="dbi-download-button-wrapper">
        <Button
          className="ns-btn--icon trend-justify-btn"
          icon={downloadLoading ? 'loading' : 'download'}
          disabled={loading}
          loading={downloadLoading}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={handleDownloadImage}
        >
          {formatMessage({ id: 'dbu_common_download' })}
        </Button>
      </div>
      {loading && (
        <div className="dbi-casual-daily-reports-loading">
          <Loading content={formatMessage({ id: 'common_loading' })} />
        </div>
      )}
      <div ref={dailyReportPageRef} className="dbi-daily-report-page" style={{ display: loading ? 'none' : 'block' }}>
        {/* Header */}
        <CasualDailyReportsHeader />

        {/* 今日核心要点 */}
        <CasualDailyReportsHighlight
          markdownConverter={markdownConverter}
          corePoints={reportDetails?.core_points}
          dom={<DateSelect availableDates={reportDates} value={reportDate} onChange={handleReportDateChange} />}
        />

        {/* 产品异动 */}
        <div
          style={style}
          className={classnames('dbi-casual-daily-reports-product-changed dbi-section-production', className)}
        >
          <div className="dbi-section-production-title">
            <img className="dbi-section-production-title-img" src={IMG_TITLE_PRODUCT} alt="Production" />
          </div>
          <ProductChangedRank
            sensortowerDownloadChangeRank={sensortowerDownloadChangeRank}
            sensortowerDauChangeRank={sensortowerDauChangeRank}
            sensortowerRevenueChangeRank={sensortowerRevenueChangeRank}
            storeRankChangeRank={storeRankChangeRank}
            popularityRanksWeixin={weixinPopularityStoreRankChangeRank}
            mostPlayedRanksWeixin={weixinMostPlayedStoreRankChangeRank}
            popularityRanksDouyin={dyPopularityStoreRankChangeRank}
            freshGameRanksDouyin={dyFreshGameStoreRankChangeRank}
            onGoToGameDetails={handleGoToGameDetailsStats}
          />
          {showProductChanged && (
            <ProductChangedCard
              markdownConverter={markdownConverter}
              renderLinker={renderLinker}
              productChanged={productChanged}
              onGoToGameDetails={handleGoToGameDetails}
            />
          )}
        </div>

        {/* 厂商异动 */}
        {/* 厂商异动超过2个样式 class增加  dbi--more */}
        {!!reportDetails?.manufacturer_changed?.length && (
          <CasualDailyReportsManufacturerChanged
            markdownConverter={markdownConverter}
            renderLinker={renderLinker}
            manufacturerChanged={reportDetails?.manufacturer_changed}
          />
        )}

        {/* 行业新闻 */}
        {/* 行业新闻大于或等于 3个时   dbi--more */}
        {!!reportDetails?.industry_news?.length && (
          <CasualDailyReportsIndustryNews
            markdownConverter={markdownConverter}
            renderLinker={renderLinker}
            industryNews={reportDetails?.industry_news}
          />
        )}

        {/* GRC */}
        <GrcReport reports={reportDetails?.grc_reports} />

        {/* 商店排行榜 */}
        {(!!reportDetails?.appstore_free_rank?.length ||
          !!reportDetails?.sensortower_dau?.length ||
          showStoreRankTop) && (
          <CasualDailyReportsRanks
            date={reportDate}
            sensortowerDauRanksDate={reportDetails?.sensortower_dau_date || ''}
            appstoreFreeRanks={reportDetails?.appstore_free_rank}
            sensortowerDauRanks={reportDetails?.sensortower_dau}
            miniGameRankModules={reportDetails?.mini_game_rank_modules}
            onGoToGameDetails={handleGoToGameDetailsStats}
          />
        )}

        {/* Footer */}
        <CasualDailyReportsFooter />
      </div>
    </div>
  );
};

export default CasualDailyReports;
